import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { PermissionRequirement } from '@/types/permission'
import { canAccessPermissionRequirement } from '@/utils/permission'

export const usePermissionStore = defineStore('permission', () => {
  const permissions = ref<string[]>([])
  let stopStorageSyncHandler: (() => void) | null = null

  const parsePermissions = (raw: string | null): string[] => {
    if (!raw) return []
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') : []
    } catch {
      return []
    }
  }

  // 设置权限列表（登录/切换租户后调用）
  const setPermissions = (perms: string[]) => {
    permissions.value = perms
    localStorage.setItem('permissions', JSON.stringify(perms))
  }

  // 从 localStorage 加载权限（页面刷新时）
  const loadPermissions = () => {
    const stored = localStorage.getItem('permissions')
    permissions.value = parsePermissions(stored)
  }

  // 检查是否拥有指定权限
  const hasPermission = (code: string): boolean => {
    return permissions.value.includes(code)
  }

  // 检查是否拥有任一权限
  const hasAnyPermission = (...codes: string[]): boolean => {
    return codes.some(code => permissions.value.includes(code))
  }

  const canAccess = (requirement?: PermissionRequirement): boolean => {
    return canAccessPermissionRequirement(requirement, permissions.value)
  }

  // 清除权限（登出时调用）
  const clearPermissions = () => {
    permissions.value = []
    localStorage.removeItem('permissions')
  }

  const startStorageSync = () => {
    if (typeof window === 'undefined' || stopStorageSyncHandler) return

    const handleStorage = (event: StorageEvent) => {
      if (event.key === 'permissions') {
        permissions.value = parsePermissions(event.newValue)
      }
    }

    window.addEventListener('storage', handleStorage)
    stopStorageSyncHandler = () => {
      window.removeEventListener('storage', handleStorage)
      stopStorageSyncHandler = null
    }
  }

  const stopStorageSync = () => {
    stopStorageSyncHandler?.()
  }

  return {
    permissions,
    setPermissions,
    loadPermissions,
    hasPermission,
    hasAnyPermission,
    canAccess,
    clearPermissions,
    startStorageSync,
    stopStorageSync,
  }
})
